import { FC, useRef, useCallback, DragEvent, useState } from 'react'
import { Plus, FileUp } from 'lucide-react'
import { cn } from '../../lib/utils'
import { DocumentCard } from './DocumentCard'
import { UnifiedFile } from '../../contexts/ChatContext'
import { validateFile, getFileInputAccept } from '../../utils/fileValidation'

interface DocumentUploadZoneProps {
  documents: UnifiedFile[]
  onAddFile: (file: File) => void
  onRemoveDocument: (id: string) => void
}

/**
 * Horizontal document upload zone that sits at the top of the chat area
 * Shows uploaded document cards and allows adding more
 */
export const DocumentUploadZone: FC<DocumentUploadZoneProps> = ({
  documents,
  onAddFile,
  onRemoveDocument
}) => {
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = useCallback((file: File) => {
    // Use centralized validation
    const validation = validateFile(file)
    if (!validation.valid) {
      console.error('File validation failed:', validation.error)
      // TODO: Show toast notification to user
      return
    }

    onAddFile(file)
  }, [onAddFile])

  const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleFileSelect(file)
  }, [handleFileSelect])

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFileSelect(file)
    e.target.value = '' // Reset for re-upload of same file
  }, [handleFileSelect])

  const readyCount = documents.filter(d => d.status === 'ready').length
  const parsingCount = documents.filter(d => d.status === 'uploading' || d.status === 'processing' || d.parsingStatus === 'pending').length

  // Don't render if no documents
  if (documents.length === 0) {
    return null
  }

  return (
    <div
      className={cn(
        "w-full px-4 py-3 border-b transition-all duration-200",
        isDragOver
          ? "bg-primary/5 border-primary/30"
          : "bg-background/50 border-border/30"
      )}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <div className="max-w-[800px] mx-auto">
        {/* Status summary */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <FileUp className="h-3.5 w-3.5" />
            <span>
              {parsingCount > 0 && `${parsingCount} parsing`}
              {parsingCount > 0 && readyCount > 0 && ' · '}
              {readyCount > 0 && `${readyCount} ready`}
            </span>
          </div>
          {documents.length > 0 && (
            <span className="text-[10px] text-muted-foreground">
              Start chatting to merge into patient context
            </span>
          )}
        </div>

        {/* Document cards row */}
        <div className="flex flex-wrap gap-2 items-start">
          {documents.map((doc) => (
            <DocumentCard
              key={doc.id}
              document={doc}
              onRemove={onRemoveDocument}
            />
          ))}

          {/* Add more button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed",
              "text-sm text-muted-foreground hover:text-foreground",
              "hover:border-primary/40 hover:bg-primary/5",
              "transition-all duration-200",
              "focus:outline-none focus:ring-2 focus:ring-primary/20"
            )}
          >
            <Plus className="h-4 w-4" />
            <span>Add more</span>
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept={getFileInputAccept()}
            onChange={handleInputChange}
            className="hidden"
          />
        </div>
      </div>
    </div>
  )
}


